// src/utils/exportUtils.ts
import { DataRecord } from '../types/DataTypes';
import { ExportConfiguration, ShareConfiguration } from '../types/ViewTypes';

/**
 * Export data to CSV format
 */
export function exportToCsv(
  data: DataRecord[],
  variables?: string[],
  includeMetadata: boolean = true
): string {
  if (data.length === 0) return '';
  
  // Determine columns to include
  const columns = variables 
    ? ['iso_r', 'region', 'year', 'country', ...variables] 
    : Object.keys(data[0]);
  
  // Create CSV header
  const header = columns.join(',');
  
  // Create CSV rows
  const rows = data.map(row => {
    return columns.map(column => {
      const value = row[column];
      
      // Handle special cases for CSV formatting
      if (value === null || value === undefined) {
        return '';
      }
      
      if (typeof value === 'string') {
        // Escape quotes and wrap in quotes
        return `"${value.replace(/"/g, '""')}"`;
      }
      
      return value;
    }).join(',');
  });
  
  // Add metadata if requested
  if (includeMetadata) {
    const metadata = [
      `# Export Date: ${new Date().toISOString()}`,
      `# Total Records: ${data.length}`,
      `# Variables: ${columns.join(', ')}`,
      `# Regions: ${[...new Set(data.map(d => d.iso_r))].length}`,
      `# Years: ${[...new Set(data.map(d => d.year))].join(', ')}`
    ].join('\n');
    
    return `${metadata}\n${header}\n${rows.join('\n')}`;
  }
  
  return `${header}\n${rows.join('\n')}`;
}

/**
 * Export data to JSON format
 */
export function exportToJson(
  data: DataRecord[],
  variables?: string[],
  prettyPrint: boolean = true
): string {
  // Filter data to only include specified variables if provided
  let filteredData = data;
  
  if (variables) {
    filteredData = data.map(record => {
      const newRecord: Record<string, any> = {
        iso_r: record.iso_r,
        region: record.region,
        year: record.year,
        country: record.country
      };
      
      variables.forEach(variable => {
        if (variable in record) {
          newRecord[variable] = record[variable];
        }
      });
      
      return newRecord as DataRecord;
    });
  }
  
  return prettyPrint 
    ? JSON.stringify(filteredData, null, 2) 
    : JSON.stringify(filteredData);
}

/**
 * Export data to XLSX format (using SheetJS in a browser environment)
 * 
 * Note: This is a placeholder implementation. In a real application,
 * you would need to include the SheetJS library and implement the
 * actual export logic.
 */
export function exportToXlsx(
  data: DataRecord[],
  variables?: string[]
): Uint8Array {
  // This is a placeholder. In a real implementation, you would use
  // SheetJS to create an Excel file.
  console.warn('XLSX export is not fully implemented in this example.');
  
  // Return empty array as placeholder
  return new Uint8Array();
}

/**
 * Download data as a file
 */
export function downloadFile(
  content: string | Uint8Array,
  filename: string,
  mimeType: string
): void {
  const blob = content instanceof Uint8Array
    ? new Blob([content], { type: mimeType })
    : new Blob([content], { type: mimeType });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Export and download data based on configuration
 */
export function exportData(
  data: DataRecord[],
  config: ExportConfiguration,
  variables?: string[]
): void {
  const { format, fileName } = config;
  
  switch (format) {
    case 'csv':
      const csvContent = exportToCsv(data, variables, config.includeFilters);
      downloadFile(csvContent, `${fileName}.csv`, 'text/csv');
      break;
    
    case 'json':
      const jsonContent = exportToJson(data, variables, true);
      downloadFile(jsonContent, `${fileName}.json`, 'application/json');
      break;
    
    case 'xlsx':
      const xlsxContent = exportToXlsx(data, variables);
      downloadFile(xlsxContent, `${fileName}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      break;
    
    default:
      console.error(`Unsupported export format: ${format}`);
  }
}

/**
 * Export chart as image
 *
 * Uses html2canvas to convert the chart element to an image.
 * Includes enhanced error handling and support for complex elements like maps.
 */
export function exportChart(
  chartElement: HTMLElement,
  config: ExportConfiguration
): Promise<void> {
  const { format, fileName } = config;

  console.log('Export starting...', {
    element: chartElement,
    elementSize: {
      width: chartElement.offsetWidth,
      height: chartElement.offsetHeight,
      scrollWidth: chartElement.scrollWidth,
      scrollHeight: chartElement.scrollHeight
    },
    format,
    fileName
  });

  // Validate element
  if (!chartElement) {
    return Promise.reject(new Error('Chart element is null or undefined'));
  }

  if (chartElement.offsetWidth === 0 || chartElement.offsetHeight === 0) {
    return Promise.reject(new Error('Chart element has no dimensions (width or height is 0)'));
  }

  // Dynamic import of html2canvas to only load when needed
  return import('html2canvas')
    .then(({ default: html2canvas }) => {
      console.log('html2canvas loaded, starting capture...');

      // Enhanced html2canvas options for better compatibility
      return html2canvas(chartElement, {
        scale: 2, // Higher scale for better quality
        logging: true, // Enable logging for debugging
        useCORS: true, // Enable CORS for images
        allowTaint: true, // Allow cross-origin images
        backgroundColor: null, // Preserve transparency
        removeContainer: false, // Keep the container
        imageTimeout: 15000, // 15 second timeout for images
        ignoreElements: (element) => {
          // Ignore elements that might cause issues
          const tagName = element.tagName.toLowerCase();
          // Ignore video and iframe elements
          if (tagName === 'video' || tagName === 'iframe') {
            console.log('Ignoring element:', tagName);
            return true;
          }
          return false;
        },
        onclone: (clonedDoc, clonedElement) => {
          console.log('Document cloned, processing styles...');

          // Fix any Leaflet map tiles that might not render correctly
          const mapContainers = clonedElement.querySelectorAll('.leaflet-container');
          mapContainers.forEach((container: any) => {
            container.style.zIndex = 'auto';
            console.log('Fixed Leaflet container z-index');
          });

          // Ensure all canvas elements are visible
          const canvases = clonedElement.querySelectorAll('canvas');
          canvases.forEach((canvas: any) => {
            canvas.style.display = 'block';
            console.log('Fixed canvas display');
          });
        }
      });
    })
    .then(canvas => {
      console.log('Canvas created:', {
        width: canvas.width,
        height: canvas.height
      });

      let mimeType = 'image/png';
      let fileExtension = 'png';
      let quality = 0.95; // Quality for JPEG

      // Set appropriate MIME type based on format
      switch (format as string) {
        case 'jpeg':
          mimeType = 'image/jpeg';
          fileExtension = 'jpg';
          break;
        case 'png':
          mimeType = 'image/png';
          fileExtension = 'png';
          break;
        case 'svg':
          console.warn('SVG export is not supported by html2canvas. Falling back to PNG.');
          mimeType = 'image/png';
          fileExtension = 'png';
          break;
        default:
          mimeType = 'image/png';
          fileExtension = 'png';
      }

      console.log('Converting canvas to blob...', { mimeType, fileExtension });

      // Convert canvas to blob
      return new Promise<void>((resolve, reject) => {
        try {
          canvas.toBlob(
            (blob: Blob | null) => {
              if (!blob) {
                reject(new Error('Failed to create blob from canvas'));
                return;
              }

              console.log('Blob created:', {
                size: blob.size,
                type: blob.type
              });

              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');

              link.href = url;
              link.download = `${fileName}.${fileExtension}`;
              link.style.display = 'none';

              document.body.appendChild(link);

              console.log('Triggering download...', link.download);
              link.click();

              // Clean up
              setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                console.log('Cleanup completed');
                resolve();
              }, 100);
            },
            mimeType,
            quality
          );
        } catch (error) {
          console.error('Error in toBlob:', error);
          reject(error);
        }
      });
    })
    .catch(error => {
      console.error('Error in export chain:', error);

      // Provide more specific error messages
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Could not load required resources. Check your internet connection.');
      } else if (error.message.includes('CORS')) {
        throw new Error('Security error: Some images could not be loaded due to CORS restrictions.');
      } else if (error.message.includes('timeout')) {
        throw new Error('Timeout error: Export took too long. Try a smaller area or simpler view.');
      } else {
        throw error;
      }
    });
}

/**
 * Generate a share URL with current state
 */
export function generateShareUrl(
  state: {
    viewMode: string;
    filters: any;
    variable?: string;
    year?: number;
    chartType?: string;
    activeTab?: string;
    showTrendlines?: boolean;
    visualizationSettings?: any;
    [key: string]: any; // Allow for additional properties
  },
  config: {
    includeFilters: boolean;
    includeViewConfiguration: boolean;
    generateShortLink: boolean;
    expiresAfter?: number;
  } = {
    includeFilters: true,
    includeViewConfiguration: true,
    generateShortLink: false
  }
): string {
  // Filter out properties based on configuration
  const filteredState = { ...state };
  
  if (!config.includeFilters) {
    delete filteredState.filters;
  }
  
  if (!config.includeViewConfiguration) {
    delete filteredState.visualizationSettings;
    // Remove other view configuration items as needed
    delete filteredState.chartType;
    delete filteredState.showTrendlines;
  }
  
  // Encode state as JSON and then as URL parameter
  const stateParam = encodeURIComponent(JSON.stringify(filteredState));
  
  // Create URL with state parameter
  const url = `${window.location.origin}${window.location.pathname}?state=${stateParam}`;
  
  // Generate short link if requested (This would typically be done via an API)
  if (config.generateShortLink) {
    console.warn('Short link generation is not implemented in this example.');
    // In a real implementation, you would call an API to create a short link
    return url;
  }
  
  return url;
}

/**
 * Parse a share URL to extract state
 */
export function parseShareUrl(url: string): any {
  const urlObj = new URL(url);
  const stateParam = urlObj.searchParams.get('state');
  
  if (!stateParam) return null;
  
  try {
    return JSON.parse(decodeURIComponent(stateParam));
  } catch (error) {
    console.error('Failed to parse share URL state', error);
    return null;
  }
}

/**
 * Social media sharing options
 */
export interface SocialShareOptions {
  url: string;
  title?: string;
  description?: string;
  hashtags?: string[];
  via?: string; // For Twitter
}

/**
 * Share on Twitter
 */
export function shareOnTwitter(options: SocialShareOptions): void {
  const { url, title, hashtags, via } = options;
  
  let twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`;
  
  if (title) {
    twitterUrl += `&text=${encodeURIComponent(title)}`;
  }
  
  if (hashtags && hashtags.length > 0) {
    twitterUrl += `&hashtags=${encodeURIComponent(hashtags.join(','))}`;
  }
  
  if (via) {
    twitterUrl += `&via=${encodeURIComponent(via)}`;
  }
  
  window.open(twitterUrl, '_blank', 'width=550,height=420');
}

/**
 * Share on LinkedIn
 */
export function shareOnLinkedIn(options: SocialShareOptions): void {
  const { url, title, description } = options;
  
  let linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  
  window.open(linkedInUrl, '_blank', 'width=550,height=420');
}

/**
 * Share on Facebook
 */
export function shareOnFacebook(options: SocialShareOptions): void {
  const { url } = options;
  
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  
  window.open(facebookUrl, '_blank', 'width=550,height=420');
}

/**
 * Share on Instagram (note: Instagram doesn't support direct link sharing via web API)
 * This function will just copy the URL to clipboard with a message explaining
 * that Instagram doesn't support direct sharing.
 */
export function shareOnInstagram(options: SocialShareOptions): void {
  navigator.clipboard.writeText(options.url)
    .then(() => {
      alert('URL copied to clipboard. Instagram does not support direct link sharing via web API. Please paste the link manually into Instagram.');
    })
    .catch(err => {
      console.error('Failed to copy URL for Instagram sharing:', err);
      alert('Failed to copy URL. Please copy it manually and share on Instagram.');
    });
}

/**
 * Share via email
 */
export function shareViaEmail(options: SocialShareOptions): void {
  const { url, title, description } = options;
  
  let subject = title || 'Regional Data Explorer Analysis';
  let body = description || 'Check out this analysis from the Regional Data Explorer:';
  body += `\n\n${url}`;
  
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  
  window.location.href = mailtoUrl;
}

/**
 * Share to all platforms from a unified function
 */
export function shareToPlatform(platform: 'twitter' | 'linkedin' | 'facebook' | 'instagram' | 'email', options: SocialShareOptions): void {
  switch (platform) {
    case 'twitter':
      shareOnTwitter(options);
      break;
    case 'linkedin':
      shareOnLinkedIn(options);
      break;
    case 'facebook':
      shareOnFacebook(options);
      break;
    case 'instagram':
      shareOnInstagram(options);
      break;
    case 'email':
      shareViaEmail(options);
      break;
    default:
      console.error('Unknown sharing platform:', platform);
  }
}

// ==================================================================
// SIMILARITY ANALYSIS EXPORT FUNCTIONALITY
// ==================================================================

/**
 * Interface for similarity analysis export data
 */
export interface SimilarityExportData {
  similarity?: {
    results: Array<{
      regionId: string;
      regionName: string;
      similarity: number;
      rank: number;
    }>;
    targetRegion: string;
    variables: string[];
    metric: string;
    parameters?: any;
  };
  clustering?: {
    clusters: Array<{
      clusterId: number;
      name: string;
      regions: string[];
      memberCount: number;
      coherence?: number;
    }>;
    variables: string[];
    algorithm: string;
    parameters?: any;
  };
  peerAnalysis?: {
    targetRegion: string;
    peerGroup: Array<{
      regionId: string;
      regionName: string;
      similarity: number;
    }>;
    performanceMetrics: {
      ranking: number;
      totalPeers: number;
      abovePeerAverage: number;
      bestInClass: string[];
      needsImprovement: string[];
    };
    variables: string[];
  };
  anomalies?: {
    anomalies: Array<{
      regionId: string;
      regionName: string;
      anomalyScore: number;
      anomalyType: string;
      method: string;
      outlierVariables: string[];
    }>;
    method: string;
    threshold?: number;
    variables: string[];
    statistics: {
      totalRegions: number;
      extremeAnomalies: number;
      moderateOutliers: number;
      normalRegions: number;
    };
  };
  heatmap?: {
    matrix: Array<{
      regionA: string;
      regionB: string;
      similarity: number;
    }>;
    variables: string[];
    metric: string;
    statistics: {
      averageSimilarity: number;
      maxSimilarity: number;
      minSimilarity: number;
      totalComparisons: number;
    };
  };
  network?: {
    nodes: Array<{
      regionId: string;
      regionName: string;
      connections: number;
    }>;
    edges: Array<{
      source: string;
      target: string;
      similarity: number;
    }>;
    variables: string[];
    metric: string;
    threshold: number;
    statistics: {
      totalNodes: number;
      totalEdges: number;
      averageConnections: number;
      networkDensity: number;
    };
  };
  metadata?: {
    analysisType: string;
    timestamp: string;
    applicationVersion: string;
    datasetInfo?: {
      totalRegions: number;
      totalVariables: number;
      timeRange?: string;
    };
  };
}

/**
 * Export similarity analysis data as CSV format
 */
export async function exportSimilarityCSV(data: SimilarityExportData, filename: string = 'similarity_analysis.csv'): Promise<void> {
  let csvContent = '';
  
  // Add metadata header
  if (data.metadata) {
    csvContent += `# Regional Data Explorer - Similarity Analysis Export\n`;
    csvContent += `# Analysis Type: ${data.metadata.analysisType}\n`;
    csvContent += `# Generated: ${data.metadata.timestamp}\n`;
    csvContent += `# Version: ${data.metadata.applicationVersion}\n\n`;
  }
  
  // Export similarity results
  if (data.similarity) {
    csvContent += `SIMILARITY ANALYSIS\n`;
    csvContent += `Target Region,${data.similarity.targetRegion}\n`;
    csvContent += `Variables,"${data.similarity.variables.join(', ')}"\n`;
    csvContent += `Metric,${data.similarity.metric}\n\n`;
    csvContent += `Rank,Region ID,Region Name,Similarity Score\n`;
    data.similarity.results.forEach(result => {
      csvContent += `${result.rank},${result.regionId},"${result.regionName}",${result.similarity.toFixed(4)}\n`;
    });
    csvContent += `\n`;
  }
  
  // Export clustering results
  if (data.clustering) {
    csvContent += `CLUSTERING ANALYSIS\n`;
    csvContent += `Algorithm,${data.clustering.algorithm}\n`;
    csvContent += `Variables,"${data.clustering.variables.join(', ')}"\n\n`;
    csvContent += `Cluster ID,Cluster Name,Member Count,Coherence,Region Members\n`;
    data.clustering.clusters.forEach(cluster => {
      const regionList = cluster.regions.join('; ');
      csvContent += `${cluster.clusterId},"${cluster.name}",${cluster.memberCount},${cluster.coherence?.toFixed(4) || 'N/A'},"${regionList}"\n`;
    });
    csvContent += `\n`;
  }
  
  // Export peer analysis results
  if (data.peerAnalysis) {
    csvContent += `PEER GROUP ANALYSIS\n`;
    csvContent += `Target Region,${data.peerAnalysis.targetRegion}\n`;
    csvContent += `Variables,"${data.peerAnalysis.variables.join(', ')}"\n`;
    csvContent += `Performance Ranking,${data.peerAnalysis.performanceMetrics.ranking} of ${data.peerAnalysis.performanceMetrics.totalPeers}\n`;
    csvContent += `Above Peer Average,${data.peerAnalysis.performanceMetrics.abovePeerAverage} variables\n`;
    csvContent += `Best-in-Class,"${data.peerAnalysis.performanceMetrics.bestInClass.join(', ')}"\n`;
    csvContent += `Needs Improvement,"${data.peerAnalysis.performanceMetrics.needsImprovement.join(', ')}"\n\n`;
    csvContent += `Region ID,Region Name,Similarity Score\n`;
    data.peerAnalysis.peerGroup.forEach(peer => {
      csvContent += `${peer.regionId},"${peer.regionName}",${peer.similarity.toFixed(4)}\n`;
    });
    csvContent += `\n`;
  }
  
  // Export anomaly detection results
  if (data.anomalies) {
    csvContent += `ANOMALY DETECTION\n`;
    csvContent += `Method,${data.anomalies.method}\n`;
    csvContent += `Threshold,${data.anomalies.threshold || 'N/A'}\n`;
    csvContent += `Variables,"${data.anomalies.variables.join(', ')}"\n`;
    csvContent += `Total Regions,${data.anomalies.statistics.totalRegions}\n`;
    csvContent += `Extreme Anomalies,${data.anomalies.statistics.extremeAnomalies}\n`;
    csvContent += `Moderate Outliers,${data.anomalies.statistics.moderateOutliers}\n`;
    csvContent += `Normal Regions,${data.anomalies.statistics.normalRegions}\n\n`;
    csvContent += `Region ID,Region Name,Anomaly Score,Anomaly Type,Method,Outlier Variables\n`;
    data.anomalies.anomalies.forEach(anomaly => {
      csvContent += `${anomaly.regionId},"${anomaly.regionName}",${anomaly.anomalyScore.toFixed(4)},"${anomaly.anomalyType}","${anomaly.method}","${anomaly.outlierVariables.join('; ')}"\n`;
    });
    csvContent += `\n`;
  }
  
  // Export heatmap data
  if (data.heatmap) {
    csvContent += `SIMILARITY HEATMAP\n`;
    csvContent += `Variables,"${data.heatmap.variables.join(', ')}"\n`;
    csvContent += `Metric,${data.heatmap.metric}\n`;
    csvContent += `Average Similarity,${data.heatmap.statistics.averageSimilarity.toFixed(4)}\n`;
    csvContent += `Max Similarity,${data.heatmap.statistics.maxSimilarity.toFixed(4)}\n`;
    csvContent += `Min Similarity,${data.heatmap.statistics.minSimilarity.toFixed(4)}\n\n`;
    csvContent += `Region A,Region B,Similarity Score\n`;
    data.heatmap.matrix.forEach(cell => {
      csvContent += `${cell.regionA},${cell.regionB},${cell.similarity.toFixed(4)}\n`;
    });
    csvContent += `\n`;
  }
  
  // Export network data
  if (data.network) {
    csvContent += `NETWORK ANALYSIS\n`;
    csvContent += `Variables,"${data.network.variables.join(', ')}"\n`;
    csvContent += `Metric,${data.network.metric}\n`;
    csvContent += `Similarity Threshold,${data.network.threshold.toFixed(2)}\n`;
    csvContent += `Total Nodes,${data.network.statistics.totalNodes}\n`;
    csvContent += `Total Edges,${data.network.statistics.totalEdges}\n`;
    csvContent += `Average Connections,${data.network.statistics.averageConnections.toFixed(2)}\n`;
    csvContent += `Network Density,${data.network.statistics.networkDensity.toFixed(4)}\n\n`;
    csvContent += `NODES\n`;
    csvContent += `Region ID,Region Name,Connections\n`;
    data.network.nodes.forEach(node => {
      csvContent += `${node.regionId},"${node.regionName}",${node.connections}\n`;
    });
    csvContent += `\nEDGES\n`;
    csvContent += `Source,Target,Similarity\n`;
    data.network.edges.forEach(edge => {
      csvContent += `${edge.source},${edge.target},${edge.similarity.toFixed(4)}\n`;
    });
  }
  
  downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
}

/**
 * Export similarity analysis data as JSON format
 */
export async function exportSimilarityJSON(data: SimilarityExportData, filename: string = 'similarity_analysis.json'): Promise<void> {
  const jsonContent = JSON.stringify(data, null, 2);
  downloadFile(jsonContent, filename, 'application/json;charset=utf-8;');
}

/**
 * Export similarity analysis report as PDF
 */
export async function exportSimilarityPDF(
  data: SimilarityExportData,
  elementId?: string,
  filename: string = 'similarity_analysis_report.pdf'
): Promise<void> {
  // Dynamic import of jsPDF
  const { default: jsPDF } = await import('jspdf');
  
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;
  
  // Helper function to add text with line wrapping
  const addText = (text: string, fontSize: number = 12, isBold: boolean = false) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    
    const lines = pdf.splitTextToSize(text, contentWidth);
    lines.forEach((line: string) => {
      if (yPosition > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.text(line, margin, yPosition);
      yPosition += fontSize * 0.5;
    });
    yPosition += 5;
  };
  
  // Add header
  addText('Regional Data Explorer - Similarity Analysis Report', 18, true);
  
  if (data.metadata) {
    addText(`Generated: ${data.metadata.timestamp}`, 10);
    addText(`Analysis Type: ${data.metadata.analysisType}`, 10);
    yPosition += 10;
  }
  
  // Add similarity analysis section
  if (data.similarity) {
    addText('Similarity Analysis Results', 14, true);
    addText(`Target Region: ${data.similarity.targetRegion}`);
    addText(`Variables: ${data.similarity.variables.join(', ')}`);
    addText(`Similarity Metric: ${data.similarity.metric}`);
    addText(`Top ${Math.min(10, data.similarity.results.length)} Most Similar Regions:`);
    
    data.similarity.results.slice(0, 10).forEach((result, index) => {
      addText(`${index + 1}. ${result.regionName} (${(result.similarity * 100).toFixed(1)}%)`);
    });
    yPosition += 10;
  }
  
  // Add clustering analysis section
  if (data.clustering) {
    addText('Clustering Analysis Results', 14, true);
    addText(`Algorithm: ${data.clustering.algorithm}`);
    addText(`Variables: ${data.clustering.variables.join(', ')}`);
    addText(`Number of Clusters: ${data.clustering.clusters.length}`);
    
    data.clustering.clusters.forEach(cluster => {
      addText(`${cluster.name}: ${cluster.memberCount} regions`);
      if (cluster.coherence) {
        addText(`  Coherence: ${(cluster.coherence * 100).toFixed(1)}%`);
      }
    });
    yPosition += 10;
  }
  
  // Add peer analysis section
  if (data.peerAnalysis) {
    addText('Peer Group Analysis Results', 14, true);
    addText(`Target Region: ${data.peerAnalysis.targetRegion}`);
    addText(`Performance Ranking: ${data.peerAnalysis.performanceMetrics.ranking} of ${data.peerAnalysis.performanceMetrics.totalPeers}`);
    addText(`Variables Above Peer Average: ${data.peerAnalysis.performanceMetrics.abovePeerAverage}`);
    
    if (data.peerAnalysis.performanceMetrics.bestInClass.length > 0) {
      addText(`Best-in-Class Variables: ${data.peerAnalysis.performanceMetrics.bestInClass.join(', ')}`);
    }
    
    if (data.peerAnalysis.performanceMetrics.needsImprovement.length > 0) {
      addText(`Areas for Improvement: ${data.peerAnalysis.performanceMetrics.needsImprovement.join(', ')}`);
    }
    yPosition += 10;
  }
  
  // Add anomaly detection section
  if (data.anomalies) {
    addText('Anomaly Detection Results', 14, true);
    addText(`Detection Method: ${data.anomalies.method}`);
    if (data.anomalies.threshold) {
      addText(`Threshold: ${data.anomalies.threshold}`);
    }
    addText(`Variables: ${data.anomalies.variables.join(', ')}`);
    addText(`Total Regions Analyzed: ${data.anomalies.statistics.totalRegions}`);
    addText(`Extreme Anomalies: ${data.anomalies.statistics.extremeAnomalies}`);
    addText(`Moderate Outliers: ${data.anomalies.statistics.moderateOutliers}`);
    addText(`Normal Regions: ${data.anomalies.statistics.normalRegions}`);
    
    if (data.anomalies.anomalies.length > 0) {
      addText('Top Anomalies:');
      data.anomalies.anomalies.slice(0, 10).forEach((anomaly, index) => {
        addText(`${index + 1}. ${anomaly.regionName} (Score: ${anomaly.anomalyScore.toFixed(1)}, Type: ${anomaly.anomalyType})`);
      });
    }
    yPosition += 10;
  }
  
  // Add visualization if element provided
  if (elementId) {
    const element = document.getElementById(elementId);
    if (element) {
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 1,
          useCORS: true
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Check if image fits on current page
        if (yPosition + imgHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        
        addText('Visualization', 14, true);
        pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
      } catch (error) {
        console.warn('Failed to include visualization in PDF:', error);
        addText('Visualization could not be included in this export.');
      }
    }
  }
  
  // Save the PDF
  pdf.save(filename);
}

/**
 * Export visualization as PNG image
 */
export async function exportSimilarityPNG(
  elementId: string, 
  filename: string = 'similarity_visualization.png',
  options: {
    width?: number;
    height?: number;
    backgroundColor?: string;
    scale?: number;
  } = {}
): Promise<void> {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with ID '${elementId}' not found`);
  }
  
  const defaultOptions = {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
    allowTaint: true,
    ...options
  };
  
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(element, defaultOptions);
    
    // Create download link
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (error) {
    console.error('PNG export failed:', error);
    throw new Error('Failed to export visualization as PNG');
  }
}

/**
 * Create metadata for similarity analysis export
 */
export function createSimilarityExportMetadata(
  analysisType: string,
  datasetInfo?: {
    totalRegions: number;
    totalVariables: number;
    timeRange?: string;
  }
): SimilarityExportData['metadata'] {
  return {
    analysisType,
    timestamp: new Date().toISOString(),
    applicationVersion: '1.0.0',
    datasetInfo
  };
}

/**
 * Similarity Export Manager class for managing different export formats
 */
export class SimilarityExportManager {
  private data: SimilarityExportData = {};
  
  setSimilarityData(data: SimilarityExportData['similarity']) {
    this.data.similarity = data;
    return this;
  }
  
  setClusteringData(data: SimilarityExportData['clustering']) {
    this.data.clustering = data;
    return this;
  }
  
  setPeerAnalysisData(data: SimilarityExportData['peerAnalysis']) {
    this.data.peerAnalysis = data;
    return this;
  }
  
  setAnomalyData(data: SimilarityExportData['anomalies']) {
    this.data.anomalies = data;
    return this;
  }
  
  setHeatmapData(data: SimilarityExportData['heatmap']) {
    this.data.heatmap = data;
    return this;
  }
  
  setNetworkData(data: SimilarityExportData['network']) {
    this.data.network = data;
    return this;
  }
  
  setMetadata(metadata: SimilarityExportData['metadata']) {
    this.data.metadata = metadata;
    return this;
  }
  
  async exportCSV(filename?: string) {
    return exportSimilarityCSV(this.data, filename);
  }
  
  async exportJSON(filename?: string) {
    return exportSimilarityJSON(this.data, filename);
  }
  
  async exportPDF(filename?: string, elementId?: string) {
    return exportSimilarityPDF(this.data, elementId, filename);
  }
  
  async exportPNG(elementId: string, filename?: string, options?: any) {
    return exportSimilarityPNG(elementId, filename, options);
  }
  
  getData() {
    return this.data;
  }
  
  reset() {
    this.data = {};
    return this;
  }
}